import L from 'leaflet'
import './map_legend.css'

const STORAGE_PREFIX = 'map.legend.'
const DOT_SIZE = 14

const escape = text => String(text ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))

export const markHtml = ({ color, ring, hollow, top }, size = DOT_SIZE, { pulse = false } = {}) => {
  const border = ring ? `3px solid ${ring}` : hollow ? `3px solid ${color}` : '2px solid #fff'
  const fill   = hollow ? '#fff' : color
  const shadow = ring ? '0 0 0 1.5px #fff, 0 1px 3px rgba(0,0,0,.35)' : '0 1px 3px rgba(0,0,0,.35)'
  const klass  = pulse && ring ? 'map-legend__mark map-legend__mark--pulse' : 'map-legend__mark'
  return `<span class="${klass}" style="width:${size}px;height:${size}px;border-radius:${top ? '22%' : '50%'};background:${fill};border:${border};box-shadow:${shadow};display:inline-block;box-sizing:border-box;color:${ring || color};"></span>`
}

export const markIcon = marker => {
  const size = marker.top ? 18 : marker.ring || marker.hollow ? 16 : DOT_SIZE
  return L.divIcon({
    className:   '',
    iconSize:    [size, size],
    iconAnchor:  [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 1],
    html:        `<div class="map-pin" style="width:${size}px;height:${size}px;">${markHtml(marker, size, { pulse: true })}</div>`
  })
}

export const dotStyle = (marker, base) => {
  if (marker.ring)   return { ...base, fillColor: marker.color, color: marker.ring, weight: 3, radius: base.radius + 1 }
  if (marker.hollow) return { ...base, fillColor: '#fff', fillOpacity: 1, color: marker.color, weight: 3 }
  return { ...base, fillColor: marker.color || base.fillColor }
}

export default class MapLegend {
  constructor(map, legend, { onChange }) {
    this.map      = map
    this.legend   = legend
    this.onChange = onChange
    this.hidden   = this._read()
    // The page filter wins over the remembered legend: a hidden type must not empty an explicit search.
    if (legend.reset) this.hidden = { groups: new Set(), states: new Set(legend.hidden_states || []) }
    if (this.collapsed === undefined) this.collapsed = map.getSize().y < 480
    this.counts   = { groups: new Map(), states: new Map() }

    const control = L.control({ position: 'bottomleft' })
    control.onAdd = () => {
      this.box = L.DomUtil.create('div', 'map-legend')
      L.DomEvent.disableClickPropagation(this.box)
      L.DomEvent.disableScrollPropagation(this.box)
      this.box.addEventListener('click', this._click)
      return this.box
    }
    control.addTo(map)
    this.control = control
  }

  visible(marker) {
    if (marker.group === undefined && marker.state === undefined) return true
    return !this.hidden.groups.has(String(marker.group ?? '')) && !this.hidden.states.has(String(marker.state ?? ''))
  }

  update(markers) {
    const groups = new Map()
    const states = new Map()
    const tops   = new Map()
    markers.forEach(marker => {
      if (marker.group === undefined && marker.state === undefined) return
      const group = String(marker.group ?? '')
      groups.set(group, (groups.get(group) || 0) + 1)
      if (marker.top) tops.set(group, (tops.get(group) || 0) + 1)
      states.set(String(marker.state), (states.get(String(marker.state)) || 0) + 1)
    })
    this.counts = { groups, states, tops }
    this._render()
  }

  applyToCards(container) {
    if (!container) return
    container.querySelectorAll('[data-map-group]').forEach(card => {
      const hide = this.hidden.groups.has(card.dataset.mapGroup || '') || this.hidden.states.has(card.dataset.mapState || '')
      card.classList.toggle('map-legend--hidden-card', hide)
    })
  }

  _render() {
    if (!this.box) return
    const groups = this.legend.groups.filter(group => this.counts.groups.has(group.key))
    const states = this.legend.states.filter(state => this.counts.states.has(state.key))
    const sample = groups[0] ? groups[0].color : '#64748b'
    const row = (kind, key, mark, label, count) => {
      const off = this.hidden[kind].has(key)
      return `<button type="button" class="map-legend__row ${off ? 'is-off' : ''}" data-kind="${kind}" data-key="${escape(key)}" aria-pressed="${!off}">
        ${mark}<span class="map-legend__label">${escape(label)}</span><span class="map-legend__count">${count}</span>
      </button>`
    }

    const hiddenCount = this.hidden.groups.size + this.hidden.states.size
    const byDefault   = this.hidden.groups.size === 0 && this._sameStates(this.legend.hidden_states || [])
    const toggle = `<button type="button" class="map-legend__title map-legend__toggle" aria-expanded="${!this.collapsed}">
        <span>${escape(this.legend.title)}${this.collapsed && hiddenCount ? ` · ${hiddenCount}` : ''}</span><i class="fas fa-chevron-${this.collapsed ? 'up' : 'down'}"></i>
      </button>`
    this.box.classList.toggle('is-empty', groups.length === 0)
    this.box.classList.toggle('is-collapsed', this.collapsed)
    if (this.collapsed) { this.box.innerHTML = toggle; return }

    this.box.innerHTML = `
      ${toggle}
      ${groups.map(group => row('groups', group.key, markHtml({ color: group.color, top: (this.counts.tops.get(group.key) || 0) * 2 > this.counts.groups.get(group.key) }, 12), group.label, this.counts.groups.get(group.key))).join('')}
      ${states.length > 1 || this.hidden.states.size ? `
        <div class="map-legend__title map-legend__title--states">${escape(this.legend.states_title)}</div>
        ${states.map(state => row('states', state.key, markHtml({ color: sample, ring: state.mark === 'ring' ? '#f20d00' : null, hollow: state.mark === 'hollow' }, 12), state.label, this.counts.states.get(state.key))).join('')}
      ` : ''}
      ${!byDefault ? `<button type="button" class="map-legend__all">${escape(this.legend.show_all)}</button>` : ''}`
  }

  _click = e => {
    if (e.target.closest('.map-legend__toggle')) {
      this.collapsed = !this.collapsed
      this._write()
      this._render()
      return
    }
    if (e.target.closest('.map-legend__all')) {
      this.hidden = { groups: new Set(), states: new Set(this.legend.hidden_states || []) }
      this._write()
      this._render()
      this.onChange()
      return
    }
    const button = e.target.closest('.map-legend__row')
    if (!button) return
    const set = this.hidden[button.dataset.kind]
    const key = button.dataset.key
    if (e.altKey || e.metaKey) {
      const all = button.dataset.kind === 'groups' ? this.counts.groups : this.counts.states
      set.clear()
      all.forEach((_count, other) => { if (other !== key) set.add(other) })
    } else if (set.has(key)) {
      set.delete(key)
    } else {
      set.add(key)
    }
    this._write()
    this._render()
    this.onChange()
  }

  _sameStates(keys) {
    return keys.length === this.hidden.states.size && keys.every(key => this.hidden.states.has(key))
  }

  _read() {
    const hidden = { groups: new Set(), states: new Set(this.legend.hidden_states || []) }
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_PREFIX + this.legend.key) || 'null')
      if (saved) {
        hidden.groups = new Set(saved.groups || [])
        hidden.states = new Set(saved.states || [])
        this.collapsed = !!saved.collapsed
      }
    } catch (_) {}
    return hidden
  }

  _write() {
    try {
      localStorage.setItem(STORAGE_PREFIX + this.legend.key, JSON.stringify({ groups: [...this.hidden.groups], states: [...this.hidden.states], collapsed: !!this.collapsed }))
    } catch (_) {}
  }
}
